/**
 * ============================================================================
 * ChangeRequestProposal — Phase 6c frontend (CCIE — Change Control Impact
 *   Engine).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /changes/new
 * Purpose: a four-step proposal wizard for raising a Change Control. On
 *          submit, the backend synchronously computes the Impact Set (CCIE
 *          compute SLO < 3 s; in practice < 1 s) and we redirect the user
 *          straight to /changes/:id/impact-set.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - Each step renders inside a single Card; the wizard chrome (stepper +
 *     footer buttons) lives outside the Card.
 *   - Submit shows "Computing impact… (sub-second)" so the user understands
 *     the multi-second pause is intentional and not a hang.
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ChangeRequestProposal.module.css';
import {
  useCreateProposalMutation,
  type ChangeType,
} from './useCreateProposalMutation';
import { ChangeTypePicker } from './ChangeTypePicker';
import { SubjectEntityPicker, type SubjectEntity } from './SubjectEntityPicker';
import { RationaleForm } from './RationaleForm';

const RATIONALE_MIN = 50;
const STEPS = [1, 2, 3, 4] as const;
type StepIndex = (typeof STEPS)[number];

export interface ChangeRequestProposalProps {
  /** Optional override for tests/Storybook so the screen renders standalone. */
  onSubmitted?: (changeId: string) => void;
}

export function ChangeRequestProposal({ onSubmitted }: ChangeRequestProposalProps = {}) {
  const { t } = useTranslation();
  // useNavigate is always called per the Rules of Hooks; when the screen is
  // rendered outside a TanStack Router context (Storybook / tests) the caller
  // should pass `onSubmitted` to short-circuit the navigation path.
  const navigate = useNavigate();

  const createM = useCreateProposalMutation();

  const [step, setStep] = useState<StepIndex>(1);
  const [changeType, setChangeType] = useState<ChangeType | null>(null);
  const [subject, setSubject] = useState<SubjectEntity | null>(null);
  const [title, setTitle] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canNext: Record<StepIndex, boolean> = {
    1: changeType != null,
    2: subject != null,
    3: title.trim().length > 0 && rationale.trim().length >= RATIONALE_MIN,
    4: true,
  };

  function goNext() {
    if (!canNext[step]) return;
    if (step < 4) setStep((s) => (s + 1) as StepIndex);
  }

  function goBack() {
    if (step > 1) setStep((s) => (s - 1) as StepIndex);
  }

  async function onSubmit() {
    setError(null);
    if (!changeType || !subject) return;
    try {
      const response = await createM.mutateAsync({
        change_type: changeType,
        subject_entity_id: subject.entity_id,
        subject_entity_kind: subject.entity_kind,
        title: title.trim(),
        rationale: rationale.trim(),
      });
      if (onSubmitted) {
        onSubmitted(response.change_id);
        return;
      }
      // Backend computed the impact set; jump straight to the review surface.
      navigate({
        to: '/changes/$id/impact-set',
        params: { id: response.change_id },
      } as never);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('changeControl.proposal.errors.serverFailed', {
              defaultValue: 'Server rejected the proposal.',
            }),
      );
    }
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('changeControl.proposal.eyebrow', { defaultValue: 'Quality' })}
        title={t('changeControl.proposal.title', {
          defaultValue: 'Propose a change control',
        })}
        subtitle={t('changeControl.proposal.subtitle', {
          defaultValue:
            'Identify the change, pick the subject, and submit — the impact engine computes the full radius synchronously.',
        })}
      />

      <ol
        className={styles.stepper}
        aria-label={t('changeControl.proposal.stepperAria', {
          defaultValue: 'Change proposal steps',
        })}
      >
        {STEPS.map((idx) => {
          const labelKey = `changeControl.proposal.stepper.step${idx}`;
          const fallback = ['Type', 'Subject', 'Rationale', 'Review'][idx - 1];
          const active = step === idx;
          const done = step > idx;
          return (
            <li
              key={idx}
              className={`${styles.stepperItem} ${active ? styles.stepperItemActive : ''} ${
                done ? styles.stepperItemDone : ''
              }`}
              aria-current={active ? 'step' : undefined}
            >
              <span className={styles.stepperNumber} aria-hidden="true">
                {idx}
              </span>
              <span className={styles.stepperLabel}>
                {t(labelKey, { defaultValue: fallback })}
              </span>
            </li>
          );
        })}
      </ol>

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t(`changeControl.proposal.stepHeading.step${step}`, {
              defaultValue: [
                'Choose change type',
                'Pick subject entity',
                'Title and rationale',
                'Review and submit',
              ][step - 1],
            })}
          </h2>
        </CardHeader>
        <CardBody>
          {error && (
            <div role="alert" className={styles.errorBanner}>
              {error}
            </div>
          )}

          {step === 1 && (
            <ChangeTypePicker value={changeType} onChange={setChangeType} />
          )}

          {step === 2 && changeType && (
            <SubjectEntityPicker
              changeType={changeType}
              value={subject}
              onChange={setSubject}
            />
          )}

          {step === 3 && (
            <RationaleForm
              title={title}
              rationale={rationale}
              onTitleChange={setTitle}
              onRationaleChange={setRationale}
            />
          )}

          {step === 4 && changeType && subject && (
            <dl
              className={styles.reviewList}
              aria-label={t('changeControl.proposal.review.dlAria', {
                defaultValue: 'Proposal summary',
              })}
            >
              <div className={styles.reviewRow}>
                <dt className={styles.reviewDt}>
                  {t('changeControl.proposal.review.type', {
                    defaultValue: 'Change type',
                  })}
                </dt>
                <dd className={styles.reviewDd}>
                  {t(`changeControl.proposal.typePicker.types.${changeType}.label`, {
                    defaultValue: changeType,
                  })}
                </dd>
              </div>
              <div className={styles.reviewRow}>
                <dt className={styles.reviewDt}>
                  {t('changeControl.proposal.review.subject', {
                    defaultValue: 'Subject',
                  })}
                </dt>
                <dd className={styles.reviewDd}>
                  <strong>{subject.display_name}</strong>{' '}
                  <span className={styles.pickedKind}>{subject.entity_kind}</span>
                </dd>
              </div>
              <div className={styles.reviewRow}>
                <dt className={styles.reviewDt}>
                  {t('changeControl.proposal.review.titleLabel', {
                    defaultValue: 'Title',
                  })}
                </dt>
                <dd className={styles.reviewDd}>{title}</dd>
              </div>
              <div className={styles.reviewRow}>
                <dt className={styles.reviewDt}>
                  {t('changeControl.proposal.review.rationale', {
                    defaultValue: 'Rationale',
                  })}
                </dt>
                <dd className={styles.reviewDd}>
                  <p className={styles.reviewRationale}>{rationale}</p>
                </dd>
              </div>
            </dl>
          )}

          {createM.isPending && (
            <div role="status" aria-live="polite" className={styles.computingBanner}>
              <span className={styles.spinner} aria-hidden="true" />
              <span>
                {t('changeControl.proposal.computing', {
                  defaultValue: 'Computing impact… (sub-second)',
                })}
              </span>
            </div>
          )}
        </CardBody>
      </Card>

      <footer className={styles.wizardFooter}>
        <Button
          variant="secondary"
          onClick={goBack}
          disabled={step === 1 || createM.isPending}
          aria-label={t('changeControl.proposal.actions.backAria', {
            defaultValue: 'Go to the previous step',
          })}
        >
          {t('common.back', { defaultValue: 'Back' })}
        </Button>

        <div className={styles.wizardFooterPrimary}>
          {step < 4 && (
            <Button
              variant="primary"
              onClick={goNext}
              disabled={!canNext[step]}
              aria-label={t('changeControl.proposal.actions.nextAria', {
                defaultValue: 'Continue to the next step',
              })}
            >
              {t('common.next', { defaultValue: 'Next' })}
            </Button>
          )}

          {step === 4 && (
            <Button
              variant="primary"
              onClick={onSubmit}
              isLoading={createM.isPending}
              aria-label={t('changeControl.proposal.actions.submitAria', {
                defaultValue: 'Submit the change proposal and compute the impact set',
              })}
            >
              {t('changeControl.proposal.actions.submit', {
                defaultValue: 'Submit and compute impact',
              })}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
